/**
 * COM-189 wave 4C — the VISIBLE-chrome Builder block registry.
 *
 * Waves 4/4B made every overlay chrome surface lazy (mobile nav drawer,
 * account dropdown popover/drawer, empty minicart), which leaves exactly three
 * chrome surfaces rendering Builder `Content` synchronously on every page:
 * the footer, the alert bar and the SOW/affiliate nav bar. This module is the
 * audited subset THOSE surfaces need — the fixture evidence
 * (tests/unit/hec/builder/__fixtures__/chrome-entries, regenerate with
 * `node scripts/audit-chrome-blocks.mjs`) shows their entries reference only:
 *
 *   footer:            Accordion family, Core:Button, EmailSignup,
 *                      IconLinksGrid, IconPicker, Link, LinkWrapper, Text,
 *                      TrustArcBanner (+ SDK builtins)
 *   affiliate-nav-bar: IconPicker, LinkWrapper, Text (+ builtins); the LMN
 *                      gate blocks are authored on this surface in the
 *                      sandbox space (not in MAIN fixtures), so they stay
 *                      registered here
 *   alert-bar-workers: Text
 *
 * The overlay-only blocks (AccountDrawer, AuthContent, GuestContent,
 * SignOutButton, PerksAndRewardsSection, TPABalance, MainNavigation) live in
 * chrome-registry.ts, whose `chromeCustomComponents` = this set + those —
 * and after wave 4C is only imported from LAZY overlay modules, so their
 * definitions leave the always-loaded layout chunk.
 *
 * HARD CONSTRAINT (same as chrome-registry.ts): a block referenced by an
 * entry but missing from the registry its surface renders with is BLANK —
 * identically on SSR and client (both use the same registry per surface), so
 * it can not hydration-mismatch, but it IS missing content. Pinned by the
 * per-model fixture test in tests/unit/hec/builder/chrome-registry.test.ts
 * and warn-logged at runtime by VisibleChromeContent. Adding a block to a
 * footer / alert-bar / affiliate-nav entry in Builder? Add its definition
 * HERE (and re-run the audit script); adding to a nav-drawer / account
 * dropdown entry? chrome-registry.ts is enough.
 *
 * Do NOT import chrome-registry.ts (or ~/hec/builder/index.ts) from this file
 * or anything reachable from the synchronous chrome surfaces — that would
 * re-weld the overlay (or full page) registry into the layout chunk.
 */
import type { RegisteredComponent } from "@builder.io/sdk-react";

import {
  AccordionDefinition,
  AccordionDetailDefinition,
  AccordionItemDefinition,
  AccordionTitleDefinition,
} from "./blocks/components/accordion";
import { AlertBarDefinition } from "./blocks/components/alert-bar";
import { LinkWrapperDefinition } from "./blocks/components/cta";
import { ButtonDefinition } from "./blocks/components/cta/button";
import { LinkDefinition } from "./blocks/components/cta/link";
import { EmailSignupDefinition } from "./blocks/components/email-signup";
import { IconLinksGridDefinition } from "./blocks/components/icon-links-grid";
import { ChromeIconDefinition } from "./blocks/components/icon-picker/chrome";
import { LmnAllowedContentDefinition } from "./blocks/components/lmn-allowed-content";
import { LmnSuppressedContentDefinition } from "./blocks/components/lmn-suppressed-content";
import { TextDefinition } from "./blocks/components/text";
import { TrustArcBannerDefinition } from "./blocks/components/trustarc-banner";
import { HeadingDefinition } from "./blocks/components/typography/heading";

export const visibleChromeCustomComponents: RegisteredComponent[] = [
  // Accordion family — footer link groups (all sites) + affiliate nav flyouts.
  // "Builder:Accordion" / "Core:Button" / "Text" are SDK built-in NAMES that
  // our registry OVERRIDES — the overrides must ship here too, or visible
  // chrome would silently fall back to the SDK's stock rendering.
  AccordionDefinition,
  AccordionItemDefinition,
  AccordionTitleDefinition,
  AccordionDetailDefinition,
  // Alert bar — audited entries are Text-only on MAIN, but the model's own
  // "AlertBar" block appears in the HSA sandbox space and is the model's
  // designed block; tiny, so keep it.
  AlertBarDefinition,
  // Footer blocks.
  EmailSignupDefinition,
  IconLinksGridDefinition,
  TrustArcBannerDefinition,
  // LMN-gated content — FSA affiliate nav (sandbox space authoring; the LMN
  // gate itself is chrome, so these belong here regardless).
  LmnAllowedContentDefinition,
  LmnSuppressedContentDefinition,
  // Shared primitives used across all chrome entries.
  TextDefinition,
  HeadingDefinition,
  LinkDefinition,
  LinkWrapperDefinition,
  ButtonDefinition,
  // NOT the full registry's IconDefinition: that one binds a map referencing
  // all ~113 icon modules (~167KB welded into this always-loaded chunk —
  // COM-188). ChromeIconDefinition registers the same "IconPicker" name and
  // inputs with the audited chrome icon subset; see its module doc for the
  // audit/fixture/runtime-guard pinning.
  ChromeIconDefinition,
];

/** Names resolvable by the visible-chrome registry (guard + tests). */
export const VISIBLE_CHROME_REGISTERED_NAMES: ReadonlySet<string> = new Set(
  visibleChromeCustomComponents.map(c => c.name),
);

/**
 * Blocks the Builder SDK ships built-in (@builder.io/sdk-react 4.2.2 —
 * extracted from lib/edge/*.js `name:` registrations). Always renderable
 * regardless of `customComponents`, so the runtime guards must not warn on
 * them. Names our registry overrides (Text, Core:Button, Builder:Accordion)
 * appear in BOTH sets — the registered names win at render time.
 */
export const BUILDER_BUILTIN_BLOCK_NAMES: ReadonlySet<string> = new Set([
  "Builder: Tabs",
  "Builder:Accordion",
  "Columns",
  "Core:Button",
  "Core:Section",
  "Custom Code",
  "Embed",
  "Form:Form",
  "Form:Input",
  "Form:Select",
  "Form:SubmitButton",
  "Form:TextArea",
  "Fragment",
  "Image",
  "PersonalizationContainer",
  "Raw:Img",
  "Slot",
  "Symbol",
  "Text",
  "Video",
]);
